import { ReactNode } from 'react';
import Tippy from '@tippyjs/react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import { PersonAvatar, initials } from 'components/person-avatar';
import { ATUACOES } from 'pages/RealizacaoDestaque/components/CamposDaRealizacao';
import { nomeDoParticipante, rostoDoParticipante } from 'pages/RealizacaoDestaque/identidade-do-participante';
import { FOCO_VISIVEL } from 'utils/foco';
import { getPerformanceCategory } from 'utils/helpers';

/** Campo do design (`.jj-field`): borda cinza, azul no foco, cinza quando só leitura. */
export const CAMPO =
  'w-full rounded-jj-field border border-content-soft bg-brand-white px-3.5 py-[11px] font-text text-base text-content ' +
  'placeholder:text-content-soft focus:border-brand-blue focus:outline-none sm:text-[13px] ' +
  'read-only:bg-surface-100 read-only:text-content disabled:bg-surface-100 disabled:text-content-soft';

/** Rótulo dos campos do design (11px, semibold, cinza). */
export const ROTULO = 'mb-1.5 block text-jj-label font-semibold text-content-soft';

/** Nome da faixa de atuação do percentual (25 Pontual, 50 Parcial, 75 Substancial, 100 Integral). */
export function nomeDaAtuacao(percentual: unknown): string | undefined {
  return ATUACOES.find(atuacao => atuacao.valor === Number(percentual))?.nome;
}

export function Iniciais({ nome, pequeno = false }: { nome?: string; pequeno?: boolean }) {
  const iniciais = initials(nome);

  return (
    <span
      aria-hidden="true"
      className={`flex shrink-0 items-center justify-center rounded-full bg-brand-green font-display font-bold text-brand-navy ${
        pequeno ? 'h-7 w-7 text-jj-micro' : 'h-9 w-9 text-xs'
      }`}
    >
      {iniciais}
    </span>
  );
}

/**
 * Rosto de uma pessoa do cadastro: avatar montado, foto ou, sem nenhum dos dois, as iniciais no círculo verde, pela
 * regra do `PersonAvatar` das telas novas. Decorativo: o nome aparece ao lado.
 */
export function RostoDaPessoa({ pessoa, pequeno = false }: { pessoa: any; pequeno?: boolean }) {
  return (
    <PersonAvatar
      {...rostoDoParticipante(pessoa)}
      initials={initials(nomeDoParticipante(pessoa))}
      size={pequeno ? 28 : 36}
      initialsClassName={`bg-brand-green font-display font-bold text-brand-navy ${pequeno ? 'text-jj-micro' : 'text-xs'}`}
    />
  );
}

/**
 * Mensagem que impede avançar (caixa rosa-clara do design para "falta percentual"). `role="alert"`: o leitor de tela
 * anuncia quando ela aparece, já que "Continuar" fica desabilitado sem dizer por quê (WCAG 4.1.3).
 */
export function AvisoDeValidacao({ children }: { children: ReactNode }) {
  return (
    <div role="alert" className="flex items-start gap-2 rounded-jj-md bg-brand-pink-light px-3.5 py-2.5 text-[11.5px] leading-normal text-brand-blue-dark">
      <FiInfo aria-hidden="true" size={15} className="mt-px shrink-0" />
      <p className="m-0">{children}</p>
    </div>
  );
}

/** Nota em fundo amarelo-claro (orientação, sigilo, situação da aprovação). */
export function Nota({ children, alerta = false }: { children: ReactNode; alerta?: boolean }) {
  return (
    <div
      className={`flex items-start gap-2.5 rounded-jj-lg bg-brand-yellow-light px-4 py-3.5 text-xs leading-relaxed text-content ${
        alerta ? 'border border-brand-yellow' : ''
      }`}
    >
      {alerta && <FiAlertTriangle aria-hidden="true" size={18} className="mt-px shrink-0 text-brand-blue" />}
      <div className="min-w-0">{children}</div>
    </div>
  );
}

interface SeletorDeAtuacaoProps {
  /** Nome da pessoa, para o nome acessível do grupo. */
  nome: string;
  /** Percentual escolhido hoje (pode vir como texto do campo oculto). */
  valor: unknown;
  onEscolher: (valor: number) => void;
}

// SPEC_DEVIATION: o design marca a opção escolhida com borda rosa (2,66:1 contra o branco). Reason: contraste de
// componente (WCAG 1.4.11); a escolhida leva borda azul (4,88:1), além de `aria-pressed`.
/** Os quatro percentuais do design (25 Pontual, 50 Parcial, 75 Substancial, 100 Integral) com a descrição de cada faixa. */
export function SeletorDeAtuacao({ nome, valor, onEscolher }: SeletorDeAtuacaoProps) {
  return (
    <div role="group" aria-label={`Percentual de participação de ${nome}`} className="grid grid-cols-4 gap-1.5">
      {ATUACOES.map(atuacao => {
        const escolhido = atuacao.valor === Number(valor);

        return (
          <Tippy
            key={atuacao.valor}
            placement="bottom"
            allowHTML={true}
            className="bg-black"
            maxWidth={300}
            content={<div>{getPerformanceCategory(atuacao.valor)}</div>}
          >
            <button
              type="button"
              aria-pressed={escolhido}
              onClick={() => onEscolher(atuacao.valor)}
              className={`flex flex-col items-center gap-0.5 rounded-jj-md border-2 px-1 py-2 transition-colors duration-jj-fast ${
                escolhido ? 'border-brand-blue bg-brand-white text-brand-blue-dark' : 'border-surface-200 bg-surface-100 text-content'
              } ${FOCO_VISIVEL}`}
            >
              <span className="font-display text-sm font-extrabold leading-none">{atuacao.valor}%</span>
              <span className="text-center text-jj-micro leading-tight">{atuacao.nome}</span>
            </button>
          </Tippy>
        );
      })}
    </div>
  );
}
